import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import api from '../../services/api';
import Authorization from '../common/Authorization';
import Notification from '../common/Notification';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    showPassword: false,
    showConfirmPassword: false,
  });

  const [uiState, setUiState] = useState({
    errorMessage: '',
    loading: false,
    notification: null,
  });

  const { password, confirmPassword, showPassword, showConfirmPassword } = formData;
  const { errorMessage, loading, notification } = uiState;

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  let { token, email } = Object.fromEntries(queryParams.entries());

  if (token?.includes(' ')) token = token.replace(/ /g, '+');

  const togglePasswordVisibility = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const passwordRequirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'At least one number', met: /\d/.test(password) },
    { text: 'At least one special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  const handleSubmit = async () => {
    if (password !== confirmPassword) {
      return setUiState((prev) => ({
        ...prev,
        errorMessage: 'Passwords do not match',
      }));
    }

    const isValid = passwordRequirements.every((req) => req.met);
    if (!isValid) {
      return setUiState((prev) => ({
        ...prev,
        errorMessage: 'Please ensure the password meets all the requirements',
      }));
    }

    try {
      setUiState((prev) => ({ ...prev, loading: true, errorMessage: '' }));
      const response = await api.auth.resetpassword(token, password, confirmPassword, email);

      if (response.ok) {
        setFormData({
          password: '',
          confirmPassword: '',
          showPassword: false,
          showConfirmPassword: false,
        });
        navigate('/login');
      } else {
        throw new Error('Reset password failed');
      }
    } catch (error) {
      setUiState({
        loading: false,
        errorMessage: 'Something went wrong. Please try again.',
        notification: {
          message: `Error during set password: ${error.message}`,
          type: 'error',
        },
      });
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-100">
      <Authorization />

      <div className="w-full md:w-1/2 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-md">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 primary-heading">Set Your Password</h2>
          <p className="mb-4 text-gray-600 text-sm md:text-base">
            Please enter your new password below
          </p>

          {errorMessage && (
            <div className="bg-red-100 text-red-700 rounded-md mb-3 px-3 py-2 text-sm">
              {errorMessage}
            </div>
          )}

          {/* Password Field */}
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={handleChange('password')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md custom-form-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('showPassword')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={handleChange('confirmPassword')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md custom-form-input"
              />
              <button
                type="button"
                onClick={() => togglePasswordVisibility('showConfirmPassword')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? (
                  <EyeOffIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            className="btn-blue-color w-full text-white font-medium py-2 px-0 rounded-[10px] focus:outline-none focus:shadow-outline"
            disabled={loading}
          >
            {loading ? 'Setting Password...' : 'Set Password & Login'}
          </button>

          {/* Password Requirements */}
          <div className="mt-6">
            <h3 className="text-base font-semibold mb-2">Password Requirements</h3>
            <ul>
              {passwordRequirements.map((req, index) => (
                <li key={index} className="flex items-center mb-1 text-sm">
                  <span className={`mr-2 ${req.met ? 'text-green-500' : 'text-red-500'}`}>
                    {req.met ? '✓' : '✗'}
                  </span>
                  <span className="text-gray-600">{req.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setUiState((prev) => ({ ...prev, notification: null }))}
        />
      )}
    </div>
  );
};

export default ResetPassword;
